import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

const fmt = (v) => new Intl.NumberFormat('sw-TZ').format(v || 0);

const emptyForm = () => ({
  description: '',
  amount: '',
  expense_date: new Date().toISOString().slice(0, 10),
  notes: '',
  member_id: '',
  transfer_type_id: '',
});

export default function Expenses() {
  const { id: groupId } = useParams();
  const { user } = useAuthStore();
  const { T } = useLang();
  const E = T.expenses;
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [expenses, setExpenses]           = useState([]);
  const [transfers, setTransfers]         = useState([]);
  const [members, setMembers]             = useState([]);
  const [transferTypes, setTransferTypes] = useState([]);
  const [group, setGroup]                 = useState(null);
  const [loading, setLoading]             = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const [deleting, setDeleting]           = useState(null);
  const [entryType, setEntryType]         = useState('expense');
  const [form, setForm]                   = useState(emptyForm());

  // Move an expense into a member-targeted Group Transfer
  const [moveTarget, setMoveTarget]       = useState(null);
  const [moveMemberId, setMoveMemberId]   = useState('');
  const [moveTypeId, setMoveTypeId]       = useState('');
  const [moveNotes, setMoveNotes]         = useState('');
  const [moveSaving, setMoveSaving]       = useState(false);
  const [moveError, setMoveError]         = useState('');

  // Manage transfer types
  const [showTypes, setShowTypes]   = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeAmount, setNewTypeAmount] = useState('');
  const [savingType, setSavingType] = useState(false);
  const [deletingType, setDeletingType] = useState(null);

  const loadTypes = () =>
    api.get(`/groups/${groupId}/transfer-types`).then(r => setTransferTypes(r.data || []));

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/groups/${groupId}`),
      api.get(`/expenses?group_id=${groupId}`),
      api.get(`/transactions?group_id=${groupId}&type=Group Transfer`),
      api.get(`/members?group_id=${groupId}`),
      api.get(`/groups/${groupId}/transfer-types`),
    ]).then(([g, e, t, m, tt]) => {
      setGroup(g.data);
      setExpenses(e.data || []);
      setTransfers(t.data || []);
      setMembers((m.data || []).filter(mb => mb.status !== 'inactive'));
      setTransferTypes(tt.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [groupId]);

  const totalExpenses  = useMemo(() => expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0), [expenses]);
  const totalTransfers = useMemo(() => transfers.reduce((s, t) => s + parseFloat(t.amount || 0), 0), [transfers]);

  const transfersByType = useMemo(() => {
    const groups = {};
    transfers.forEach(t => {
      const key = t.sub_type || 'Msiba';
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return groups;
  }, [transfers]);

  const resetForm = () => {
    setForm(emptyForm());
    setEntryType('expense');
    setError('');
  };

  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    const selected = transferTypes.find(t => String(t.id) === String(typeId));
    setForm(f => ({
      ...f,
      transfer_type_id: typeId,
      amount: selected?.amount && parseFloat(selected.amount) > 0 ? String(parseFloat(selected.amount)) : f.amount,
    }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError('');
    if (!form.amount || Number(form.amount) <= 0) { setError(E.errAmount); return; }

    setSaving(true);
    try {
      if (entryType === 'Group Transfer') {
        if (!form.member_id) { setError(E.errMember); setSaving(false); return; }
        const selectedType = transferTypes.find(t => String(t.id) === String(form.transfer_type_id));
        const notesValue = selectedType
          ? (form.notes ? `${selectedType.name}: ${form.notes}` : selectedType.name)
          : (form.notes || null);
        await api.post('/transactions', {
          group_id: Number(groupId),
          member_id: Number(form.member_id),
          amount: parseFloat(form.amount),
          type: 'Group Transfer',
          transaction_date: form.expense_date,
          notes: notesValue,
        });
      } else {
        if (!form.description) { setError(E.errDesc); setSaving(false); return; }
        await api.post('/expenses', {
          group_id: groupId,
          description: form.description,
          amount: parseFloat(form.amount),
          expense_date: form.expense_date,
          notes: form.notes || null,
        });
      }
      setShowForm(false);
      resetForm();
      load();
    } catch (err) {
      setError(err.response?.data?.error || E.errSave);
    } finally {
      setSaving(false);
    }
  };

  const openMove = (exp) => {
    setMoveTarget(exp);
    setMoveMemberId('');
    setMoveTypeId('');
    setMoveNotes('');
    setMoveError('');
  };

  const submitMove = async (e) => {
    e.preventDefault();
    const selectedType = transferTypes.find(t => String(t.id) === String(moveTypeId));
    const subType = selectedType?.name || '';
    if (!moveMemberId || !subType) { setMoveError('Chagua mwanachama na aina ya shughuli'); return; }
    setMoveSaving(true);
    setMoveError('');
    try {
      await api.post(`/expenses/${moveTarget.id}/move-to-transfer`, {
        member_id: moveMemberId,
        sub_type: subType,
        notes: moveNotes,
      });
      setMoveTarget(null);
      load();
    } catch (err) {
      setMoveError(err.response?.data?.error || 'Imeshindwa kuhamisha');
    } finally {
      setMoveSaving(false);
    }
  };

  const handleDeleteExpense = async (expId) => {
    if (!confirm(E.deleteExpenseConfirm)) return;
    setDeleting(expId);
    try {
      await api.delete(`/expenses/${expId}`);
      setExpenses(prev => prev.filter(e => e.id !== expId));
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteTransfer = async (t) => {
    const name = t.linked_member_name || t.member_name;
    if (!confirm(`${T.common.delete}: ${name} (${fmt(t.amount)} TZS)?`)) return;
    setDeleting('t' + t.id);
    try {
      await api.delete(`/transactions/${t.id}`);
      setTransfers(prev => prev.filter(x => x.id !== t.id));
    } finally {
      setDeleting(null);
    }
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    if (!newTypeName.trim()) return;
    setSavingType(true);
    try {
      await api.post(`/groups/${groupId}/transfer-types`, {
        name: newTypeName.trim(),
        amount: newTypeAmount ? parseFloat(newTypeAmount) : 0,
      });
      setNewTypeName('');
      setNewTypeAmount('');
      await loadTypes();
    } catch {
      // silently fail
    } finally {
      setSavingType(false);
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!confirm('Futa aina hii ya msiba?')) return;
    setDeletingType(typeId);
    try {
      await api.delete(`/groups/${groupId}/transfer-types/${typeId}`);
      setTransferTypes(prev => prev.filter(t => t.id !== typeId));
    } finally {
      setDeletingType(null);
    }
  };

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common.loading}</div>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(135deg,rgba(15,118,110,0.96),rgba(15,23,42,0.94))] px-5 py-6 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:px-6">
        <Link to={`/groups/${groupId}`} className="inline-flex rounded-full bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-50">
          ← {group?.name || T.nav.groups}
        </Link>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold sm:text-3xl">{E.title}</h1>
            <p className="mt-1 text-xs text-emerald-50/80 sm:text-sm">{E.subtitle}</p>
          </div>
          <div className="hidden sm:flex h-16 w-16 shrink-0 items-center justify-center rounded-[24px] bg-white/12 text-3xl shadow-inner shadow-white/10">
            🧾
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white/10 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-200/70">{E.summaryRegular}</p>
            <p className="mt-1 text-xl font-bold">{fmt(totalExpenses)} TZS</p>
          </div>
          <div className="rounded-2xl bg-white/10 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-rose-200/70">{E.summaryMsiba}</p>
            <p className="mt-1 text-xl font-bold text-rose-200">{fmt(totalTransfers)} TZS</p>
            {Object.keys(transfersByType).length > 1 && (
              <div className="mt-1 flex flex-wrap gap-2">
                {Object.entries(transfersByType).map(([st, items]) => (
                  <span key={st} className="text-[10px] font-semibold text-white/70">
                    {st === 'Msiba' ? '🏥' : st === 'Harusi' ? '💍' : st === 'Ugonjwa' ? '🤒' : '📋'} {st}: {fmt(items.reduce((s,t)=>s+parseFloat(t.amount||0),0))}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add button */}
      {isAdmin && !showForm && (
        <button
          onClick={() => { setShowForm(true); resetForm(); }}
          className="w-full rounded-2xl border-2 border-dashed border-green-300 bg-green-50 py-4 text-sm font-semibold text-green-700 hover:bg-green-100 transition-colors"
        >
          {E.addBtn}
        </button>
      )}

      {/* Add form */}
      {isAdmin && showForm && (
        <div className="rounded-[26px] border border-white/75 bg-white/88 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
          <h2 className="text-base font-bold text-gray-800 mb-4">{E.formTitle}</h2>
          {error && <div className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Entry type */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{E.typeLabel}</label>
              <select
                value={entryType}
                onChange={e => { setEntryType(e.target.value); setError(''); setForm(f => ({ ...f, transfer_type_id: '', member_id: '' })); }}
                className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="expense">{E.typeRegular}</option>
                <option value="Group Transfer">{E.typeMsiba}</option>
              </select>
            </div>

            {/* Group Transfer fields */}
            {entryType === 'Group Transfer' && (
              <>
                {/* Transfer type sub-dropdown */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{E.msibaTypeLabel}</label>
                  <select
                    value={form.transfer_type_id}
                    onChange={handleTypeChange}
                    className="w-full rounded-xl border border-rose-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                  >
                    <option value="">{E.msibaTypeSelect}</option>
                    {transferTypes.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name}{parseFloat(t.amount) > 0 ? ` — ${fmt(t.amount)} TZS` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Member picker */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{E.memberLabel}</label>
                  <select
                    value={form.member_id}
                    onChange={e => setForm(f => ({ ...f, member_id: e.target.value }))}
                    className="w-full rounded-xl border border-rose-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                    required
                  >
                    <option value="">{E.memberSelect}</option>
                    {members.map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.phone})</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Regular expense: description */}
            {entryType === 'expense' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{E.descLabel}</label>
                <input
                  type="text"
                  placeholder={E.descPlaceholder}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{T.common.amount} (TZS) *</label>
                <input
                  type="number"
                  min="1"
                  placeholder="0"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{T.common.date} *</label>
                <input
                  type="date"
                  value={form.expense_date}
                  onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                {entryType === 'Group Transfer' ? E.notesLabel : E.notesLabelMsiba}
              </label>
              <input
                type="text"
                placeholder={entryType === 'Group Transfer' ? E.notesPlaceholderMsiba : E.notesPlaceholder}
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                className="flex-1 rounded-xl border border-gray-300 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                {T.common.cancel}
              </button>
              <button type="submit" disabled={saving}
                className={`flex-1 rounded-xl py-2.5 text-sm font-medium text-white disabled:opacity-60 ${
                  entryType === 'Group Transfer' ? 'bg-rose-700 hover:bg-rose-800' : 'bg-green-700 hover:bg-green-800'
                }`}>
                {saving ? E.saving : T.common.save}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Transfer Types Management (admin only) */}
      {isAdmin && (
        <div className="rounded-[26px] border border-rose-100 bg-rose-50/40 overflow-hidden">
          <button
            onClick={() => setShowTypes(v => !v)}
            className="w-full flex items-center justify-between px-5 py-4 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">🏥</span>
              <div>
                <p className="text-sm font-semibold text-rose-800">{E.typesMgmt}</p>
                <p className="text-xs text-rose-600">{transferTypes.length} {E.typesCount}</p>
              </div>
            </div>
            <span className="text-rose-400 text-sm">{showTypes ? '▲' : '▼'}</span>
          </button>

          {showTypes && (
            <div className="px-5 pb-5 space-y-3 border-t border-rose-100">
              <div className="space-y-2 pt-3">
                {transferTypes.length === 0 && (
                  <p className="text-xs text-rose-400 text-center py-2">{E.typesEmpty}</p>
                )}
                {transferTypes.map(t => (
                  <div key={t.id} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-rose-100 px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800">{t.name}</p>
                      {parseFloat(t.amount) > 0 && (
                        <p className="text-xs text-rose-600">{fmt(t.amount)} TZS</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteType(t.id)}
                      disabled={deletingType === t.id}
                      className="shrink-0 text-xs text-rose-400 hover:text-rose-700 disabled:opacity-50 font-medium"
                    >
                      {deletingType === t.id ? '...' : T.common.delete}
                    </button>
                  </div>
                ))}
              </div>

              <form onSubmit={handleAddType} className="flex gap-2 pt-1">
                <input
                  type="text"
                  placeholder={E.typeNamePlaceholder}
                  value={newTypeName}
                  onChange={e => setNewTypeName(e.target.value)}
                  className="flex-1 rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                />
                <input
                  type="number"
                  placeholder={E.typeAmountPlaceholder}
                  min="0"
                  value={newTypeAmount}
                  onChange={e => setNewTypeAmount(e.target.value)}
                  className="w-28 rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                />
                <button
                  type="submit"
                  disabled={savingType || !newTypeName.trim()}
                  className="rounded-xl bg-rose-700 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-800 disabled:opacity-50 shrink-0"
                >
                  {savingType ? '…' : E.typeAddBtn}
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Regular Expenses list */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 px-1">{E.sectionRegular}</h2>
        {expenses.length === 0 ? (
          <div className="text-center py-10 text-gray-400 rounded-[26px] border border-white/75 bg-white/88">
            {E.noRegular}
          </div>
        ) : (
          expenses.map(exp => (
            <div key={exp.id} className="rounded-[24px] border border-white/75 bg-white/88 p-4 shadow-[0_14px_35px_rgba(15,23,42,0.06)]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 leading-snug">{exp.description}</p>
                  {exp.notes && <p className="mt-0.5 text-xs text-slate-400">{exp.notes}</p>}
                  <p className="mt-1 text-xs text-slate-400">
                    {new Date(exp.expense_date).toLocaleDateString('sw-TZ', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold text-red-600">{fmt(exp.amount)}</p>
                  <p className="text-xs text-red-400">TZS</p>
                  {isAdmin && (
                    <div className="mt-2 flex flex-col items-end gap-1">
                      <button
                        onClick={() => openMove(exp)}
                        className="rounded-full bg-rose-50 px-3 py-0.5 text-xs font-medium text-rose-600 hover:bg-rose-100"
                      >
                        🔗 Hamisha
                      </button>
                      <button
                        onClick={() => handleDeleteExpense(exp.id)}
                        disabled={deleting === exp.id}
                        className="rounded-full bg-gray-50 px-3 py-0.5 text-xs font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                      >
                        {deleting === exp.id ? '...' : T.common.delete}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        {expenses.length > 0 && (
          <div className="rounded-2xl bg-red-50 border border-red-200 px-5 py-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-red-700">{T.common.total}</span>
            <span className="text-lg font-bold text-red-700">{fmt(totalExpenses)} TZS</span>
          </div>
        )}
      </div>

      {/* Group Transfers list — grouped by sub_type */}
      <div className="space-y-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 px-1">{E.sectionMsiba}</h2>

        {transfers.length === 0 ? (
          <div className="text-center py-10 text-gray-400 rounded-[26px] border border-white/75 bg-white/88">
            {E.noMsiba}
          </div>
        ) : (
          <>
            {Object.entries(transfersByType).map(([subType, items]) => {
              const subTotal = items.reduce((s, t) => s + parseFloat(t.amount || 0), 0);
              const isMsiba  = subType === 'Msiba';
              const isHarusi = subType === 'Harusi';
              const isUgonjwa = subType === 'Ugonjwa';
              const icon     = isMsiba ? '🏥' : isHarusi ? '💍' : isUgonjwa ? '🤒' : '📋';
              const color    = isMsiba
                ? { card: 'border-rose-100 bg-rose-50/60', badge: 'bg-rose-100 text-rose-700', header: 'text-rose-800', amt: 'text-rose-700' }
                : isHarusi
                  ? { card: 'border-pink-100 bg-pink-50/60', badge: 'bg-pink-100 text-pink-700', header: 'text-pink-800', amt: 'text-pink-700' }
                  : isUgonjwa
                    ? { card: 'border-orange-100 bg-orange-50/60', badge: 'bg-orange-100 text-orange-700', header: 'text-orange-800', amt: 'text-orange-700' }
                    : { card: 'border-blue-100 bg-blue-50/60', badge: 'bg-blue-100 text-blue-700', header: 'text-blue-800', amt: 'text-blue-700' };

              return (
                <div key={subType} className="space-y-2">
                  {/* Sub-group header */}
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{icon}</span>
                      <span className={`text-sm font-bold ${color.header}`}>{subType}</span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">{items.length}</span>
                    </div>
                    <span className={`rounded-full px-3 py-0.5 text-xs font-bold ${color.badge}`}>{fmt(subTotal)} TZS</span>
                  </div>

                  {items.map(t => (
                    <div key={t.id} className={`rounded-[24px] border p-4 shadow-[0_14px_35px_rgba(15,23,42,0.06)] ${color.card}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-800">{t.linked_member_name || t.member_name}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{t.member_phone}</p>
                          {t.notes && <p className={`text-xs mt-1 font-medium ${isMsiba ? 'text-rose-600' : isHarusi ? 'text-pink-600' : isUgonjwa ? 'text-orange-600' : 'text-blue-600'}`}>{t.notes}</p>}
                          <p className="mt-1 text-xs text-slate-400">
                            {new Date(t.transaction_date).toLocaleDateString('sw-TZ', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`text-lg font-bold ${color.amt}`}>{fmt(t.amount)}</p>
                          <p className="text-xs text-slate-400">TZS</p>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteTransfer(t)}
                              disabled={deleting === 't' + t.id}
                              className="mt-2 rounded-full bg-white/70 px-3 py-0.5 text-xs font-medium text-slate-500 hover:bg-white disabled:opacity-50"
                            >
                              {deleting === 't' + t.id ? '...' : T.common.delete}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Grand total */}
            <div className="rounded-2xl bg-slate-800 px-5 py-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-white">{T.common.total}</span>
              <span className="text-lg font-bold text-white">{fmt(totalTransfers)} TZS</span>
            </div>
          </>
        )}
      </div>

      {/* Move expense → Group Transfer modal */}
      {moveTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-1">Hamisha kwenda Group Transfer</h2>
            <p className="text-sm text-gray-500 mb-5">
              "{moveTarget.description}" — {fmt(moveTarget.amount)} TZS. Chagua mwanachama aliyepewa fedha hizi na shughuli halisi.
            </p>
            {moveError && <div className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm">{moveError}</div>}
            <form onSubmit={submitMove} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Mwanachama aliyepewa *</label>
                <select required value={moveMemberId} onChange={e => setMoveMemberId(e.target.value)}
                  className="w-full rounded-xl border border-rose-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400">
                  <option value="">Chagua mwanachama</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.phone})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Shughuli *</label>
                <select required value={moveTypeId} onChange={e => setMoveTypeId(e.target.value)}
                  className="w-full rounded-xl border border-rose-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400">
                  <option value="">Chagua aina</option>
                  {transferTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Maelezo (hiari)</label>
                <input type="text" value={moveNotes} onChange={e => setMoveNotes(e.target.value)}
                  placeholder="Mfano: Msiba wa baba yake"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setMoveTarget(null)}
                  className="flex-1 rounded-xl border border-gray-300 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  {T.common.cancel}
                </button>
                <button type="submit" disabled={moveSaving}
                  className="flex-1 rounded-xl bg-rose-700 py-2.5 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-60">
                  {moveSaving ? '...' : 'Hamisha'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
